import * as React from 'react';
import { X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClearButtonLabels } from '@repo/ui-contracts';
import { cn } from '../lib/utils';
import { Field, FieldLabel, FieldMessage, FieldHint } from './field';

const inputVariants = cva(
  'w-full rounded-md border bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-disabled disabled:bg-muted',
  {
    variants: {
      size: {
        lg: 'h-control-md px-control-x-sm text-sm',
        md: 'h-control-base px-control-x-sm text-sm',
        sm: 'h-control-sm px-control-x-xs text-xs',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  helperText?: string;
  onClear?: () => void;
  showClearButton?: boolean;
  clearButtonLabel?: ClearButtonLabels['clear'];
  clearButtonIcon?: React.ReactNode;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  error?: string | boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size,
      label,
      helperText,
      onClear,
      showClearButton = true,
      clearButtonLabel,
      clearButtonIcon,
      leftElement,
      rightElement,
      error,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Track value from props or internal state
    const currentValue = props.value !== undefined ? String(props.value) : internalValue;
    const hasValue = currentValue.length > 0;
    const shouldShowClear =
      showClearButton && isFocused && hasValue && !props.disabled && !props.readOnly;

    const hasError = Boolean(error);
    const errorMessage = typeof error === 'string' ? error : undefined;

    const handleClear = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onClear) {
        onClear();
      } else if (inputRef.current) {
        // Create and dispatch input event to trigger onChange
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(inputRef.current, '');
          const event = new Event('input', { bubbles: true });
          inputRef.current.dispatchEvent(event);
        }
      }
      setInternalValue('');
      // Keep focus on input
      inputRef.current?.focus();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      props.onChange?.(e);
    };

    const hasLeftElement = Boolean(leftElement);
    const hasRightElement = rightElement || shouldShowClear;
    const rightPadding = rightElement && shouldShowClear ? 'pr-16' : hasRightElement ? 'pr-10' : '';
    const clearAriaLabel = clearButtonLabel ?? label ?? props['aria-label'];
    const isSearchInput = type === 'search';
    const searchInputClass = isSearchInput
      ? '[appearance:textfield] [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none'
      : '';

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Delay hiding clear button to allow click to register
      setTimeout(() => setIsFocused(false), 150);
      props.onBlur?.(e);
    };

    const iconSize = size === 'sm' ? 14 : 18;
    const hasField = label || helperText || errorMessage;

    const inputElement = (
      <div className="relative">
        {leftElement && (
          <div className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 flex items-center">
            {leftElement}
          </div>
        )}
        <input
          {...props}
          type={type}
          aria-invalid={hasError || undefined}
          style={hasLeftElement ? { paddingLeft: '1.5rem', ...props.style } : props.style}
          className={cn(
            inputVariants({ size }),
            hasError ? 'border-destructive bg-error-50 dark:bg-error-900/20' : 'border-input',
            rightPadding,
            searchInputClass,
            className,
          )}
          ref={inputRef}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {(shouldShowClear || rightElement) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {shouldShowClear && (
              <button
                type="button"
                onMouseDown={handleClear}
                className="cursor-pointer text-muted-foreground/60 transition-colors hover:text-muted-foreground"
                aria-label={clearAriaLabel}
                tabIndex={-1}
              >
                {clearButtonIcon ?? <X size={iconSize} />}
              </button>
            )}
            {rightElement}
          </div>
        )}
      </div>
    );

    if (!hasField) return inputElement;

    return (
      <Field>
        {label ? <FieldLabel htmlFor={props.id}>{label}</FieldLabel> : null}
        {inputElement}
        {errorMessage ? (
          <FieldMessage>{errorMessage}</FieldMessage>
        ) : helperText ? (
          <FieldHint>{helperText}</FieldHint>
        ) : null}
      </Field>
    );
  },
);

Input.displayName = 'Input';

export { Input, inputVariants };
